import { getHsToken, handleSessionExpired } from "../happyschool_static/hsAuth";

// Toàn bộ truy cập dữ liệu quiz3 đi qua API saola-quiz3-api (không gọi thẳng
// Firebase Database từ FE nữa). Đăng nhập KHÔNG còn qua Firebase Auth — dùng
// chung token riêng (JWT) với HappySchool, xem hsAuth.js.
export const QUIZ3_API_BASE = process.env.REACT_APP_QUIZ3_API_BASE || "http://localhost:4002";

// Catalog (môn/chủ đề/thông tin đề) dùng chung cả phiên — xem fetchCatalog().
let catalogPromise = null;

async function getIdToken() {
  const token = getHsToken();
  if (!token) throw new Error("Bạn cần đăng nhập.");
  return token;
}

// GET công khai (nội dung quiz, không kèm đáp án đúng).
async function apiGet(path) {
  const res = await fetch(`${QUIZ3_API_BASE}${path}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Lỗi tải dữ liệu.");
  return data;
}

// Gọi API có xác thực (đính kèm idToken qua header Authorization).
async function apiSend(method, path, body) {
  const idToken = await getIdToken();
  const res = await fetch(`${QUIZ3_API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) handleSessionExpired();
  if (!res.ok) throw new Error(data.error || "Lỗi thao tác dữ liệu.");
  // Có thao tác ghi (tạo/sửa/xoá đề, chủ đề, lý thuyết...) -> catalog đang giữ
  // trong bộ nhớ đã cũ, lần sau phải tải lại.
  if (method !== "GET") catalogPromise = null;
  return data;
}

// ---- Đọc dữ liệu ----------------------------------------------------------

// Lấy toàn bộ subjects/topics/questions/quizzes trong 1 lần gọi.
export function fetchAllData() {
  return apiGet("/api/all");
}

// ---- API NHẸ (trang của học sinh) -----------------------------------------
// /api/all (~1,3MB, cả kho ~2000 câu hỏi) chỉ còn dùng cho trang của giáo viên
// thật sự cần nội dung câu hỏi (ngân hàng câu hỏi, tạo đề, in đề, quét phiếu).
// Trang học sinh dùng:
//   fetchCatalog()      — môn + chủ đề + THÔNG TIN đề, ~8% dung lượng /api/all
//   fetchQuizPlay(id)   — câu hỏi của ĐÚNG 1 lượt làm bài (vài KB)
// Server chưa có API mới (404 của chính Express, không có body.error) -> tự lùi
// về /api/all như cũ, để deploy FE/BE theo thứ tự nào cũng không hỏng.

// Express trả 404 HTML khi KHÔNG có route -> phân biệt với 404 "không tìm thấy đề".
class RouteMissingError extends Error {}

async function apiGetLite(path) {
  const res = await fetch(`${QUIZ3_API_BASE}${path}`);
  const data = await res.json().catch(() => null);
  if (res.status === 404 && !(data && data.error)) throw new RouteMissingError(path);
  if (!res.ok) throw new Error((data && data.error) || "Lỗi tải dữ liệu.");
  return data;
}

// Dùng chung 1 lần tải cho cả phiên (banner + trang chọn đề + trang làm bài
// trước đây MỖI TRANG tự gọi /api/all 1 lần). Lỗi thì xoá để lần sau thử lại.
export function fetchCatalog() {
  if (!catalogPromise) {
    catalogPromise = apiGetLite("/api/catalog")
      .then((d) => ({ ...d, lite: true }))
      .catch((e) => {
        if (!(e instanceof RouteMissingError)) throw e;
        return fetchAllData().then((d) => ({ ...d, lite: false }));
      })
      .catch((e) => { catalogPromise = null; throw e; });
  }
  return catalogPromise;
}

// Câu hỏi cho 1 lượt làm bài: [{ id, content, options }] — đáp án đã xáo, KHÔNG
// kèm đáp án đúng/giải thích (giải thích trả về trong kết quả /api/grade-quiz).
// Đề tổng hợp: server bốc ngẫu nhiên mỗi lần gọi.
export async function fetchQuizPlay(quizId) {
  try {
    const data = await apiGetLite(`/api/quizzes/${quizId}/play`);
    return data.questions || [];
  } catch (e) {
    if (!(e instanceof RouteMissingError)) throw e;
    // Server cũ: tự dựng bộ câu từ /api/all như trước.
    const { questions: allQuestions = {}, quizzes = {}, topics = {} } = await fetchAllData();
    const quizData = quizzes[quizId];
    if (!quizData) throw new Error("Không tìm thấy đề thi này.");
    let chosenIds;
    if (quizData.type === "fixed") {
      chosenIds = quizData.questionIds || [];
    } else {
      const byParent = buildTopicChildrenMap(topics);
      const allowed = quizData.topicId && quizData.poolScope !== "subject"
        ? new Set(getDescendantTopicIds(byParent, quizData.topicId))
        : null;
      const pool = Object.entries(allQuestions)
        .filter(([, q]) => q.subjectId === quizData.subjectId && (!allowed || allowed.has(q.topicId)))
        .map(([id]) => id);
      chosenIds = shuffle(pool).slice(0, quizData.numQuestions || 20);
    }
    return chosenIds
      .filter((id) => allQuestions[id])
      .map((id) => ({ ...allQuestions[id], id, options: shuffle(allQuestions[id].options || []) }));
  }
}

// Bản nhẹ chỉ có tên môn học/chủ đề — dùng riêng cho cây duyệt
// (Quiz3TreeBrowser), tránh phải tải kèm toàn bộ câu hỏi/đề (rất nặng,
// dễ tải không xong trên mạng di động).
export function fetchTree() {
  return apiGet("/api/tree");
}

// Lịch sử làm bài của chính mình.
export async function fetchAttempts() {
  const data = await apiSend("GET", "/api/attempts");
  return data.attempts || {};
}

// Lấy dữ liệu 1 topic (theory, video, name...).
export async function fetchTopic(topicId) {
  const data = await apiGet(`/api/topics/${topicId}`);
  return data.topic || null;
}

// ---- Cây môn học / chủ đề (QA-01) ----------------------------------------

export function createSubject(name) {
  return apiSend("POST", "/api/subjects", { name });
}

export function renameSubject(subjectId, name) {
  return apiSend("PUT", `/api/subjects/${subjectId}`, { name });
}

export function createTopic(subjectId, parentTopicId, name) {
  return apiSend("POST", "/api/topics", { subjectId, parentTopicId: parentTopicId || null, name });
}

export function renameTopic(topicId, name) {
  return apiSend("PUT", `/api/topics/${topicId}`, { name });
}

// Nội dung lý thuyết gắn với 1 topic, hiển thị ở trang Làm bài trước danh sách đề.
export function updateTopicTheory(topicId, theory) {
  return apiSend("PUT", `/api/topics/${topicId}`, { theory: theory || null });
}

// Link video bài giảng (YouTube) gắn với 1 topic.
export function updateTopicVideo(topicId, video) {
  return apiSend("PUT", `/api/topics/${topicId}`, { video: video || null });
}

// Trích videoId từ 1 link YouTube để nhúng iframe; null nếu không nhận diện được.
export function getYoutubeEmbedUrl(url) {
  if (!url) return null;
  const match = String(url).match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}

// So sánh tên topic theo số thứ tự đứng đầu (vd "3. Cài đặt React" -> 3).
function compareTopicName(a, b) {
  const numA = parseFloat(String(a.name || "").match(/^\s*(\d+(\.\d+)*)/)?.[1]);
  const numB = parseFloat(String(b.name || "").match(/^\s*(\d+(\.\d+)*)/)?.[1]);
  if (!isNaN(numA) && !isNaN(numB) && numA !== numB) return numA - numB;
  if (!isNaN(numA) && isNaN(numB)) return -1;
  if (isNaN(numA) && !isNaN(numB)) return 1;
  return String(a.name || "").localeCompare(String(b.name || ""));
}

// Gom danh sách topic thành map: parentKey -> [ {id, ...topic} ], sắp theo số đầu tên.
export function buildTopicChildrenMap(topicsObj) {
  const byParent = {};
  Object.entries(topicsObj || {}).forEach(([id, t]) => {
    const key = t.parentTopicId || `root:${t.subjectId}`;
    if (!byParent[key]) byParent[key] = [];
    byParent[key].push({ id, ...t });
  });
  Object.values(byParent).forEach((list) => list.sort(compareTopicName));
  return byParent;
}

// Đệ quy lấy toàn bộ topicId con (và chính topicId truyền vào) — cho đề tổng hợp.
export function getDescendantTopicIds(byParent, topicId) {
  const result = [topicId];
  const stack = [topicId];
  while (stack.length) {
    const current = stack.pop();
    const children = byParent[current] || [];
    children.forEach((c) => {
      result.push(c.id);
      stack.push(c.id);
    });
  }
  return result;
}

// ---- Ngân hàng câu hỏi (QA-02..05) ----------------------------------------
// Đáp án đúng (correctOptionId) do server lưu tách sang node "answerKeys".

export async function createQuestion(data) {
  const res = await apiSend("POST", "/api/questions", data);
  return res.id;
}

export function updateQuestion(questionId, data) {
  return apiSend("PUT", `/api/questions/${questionId}`, data);
}

// Đáp án đúng của 1 câu — chỉ người tạo mới đọc được (để sửa).
export async function fetchAnswerKey(questionId) {
  const data = await apiSend("GET", `/api/answer-key/${questionId}`);
  return data.correctOptionId ?? null;
}

export function deleteQuestion(questionId) {
  return apiSend("DELETE", `/api/questions/${questionId}`);
}

// ---- Đề thi (QA-06 / QA-06b) ----------------------------------------------

// displayMode: "animated" (chống chụp ảnh AI: câu hỏi chạy rồi mới hiện đáp án — chỉ khi chọn) |
// "both" (hiện cả câu hỏi và đáp án cùng lúc).
export async function createFixedQuiz({ title, subjectId, topicId, timeLimitSeconds, timePerQuestionSeconds, questionIds, displayMode }) {
  const res = await apiSend("POST", "/api/quizzes", {
    type: "fixed", title, subjectId, topicId: topicId || null,
    timeLimitSeconds, timePerQuestionSeconds, questionIds, displayMode,
  });
  return res.id;
}

export async function createSyntheticQuiz({ title, subjectId, topicId, numQuestions, timeLimitSeconds, timePerQuestionSeconds, displayMode }) {
  const res = await apiSend("POST", "/api/quizzes", {
    type: "synthetic", title, subjectId, topicId: topicId || null,
    numQuestions, timeLimitSeconds, timePerQuestionSeconds, displayMode,
  });
  return res.id;
}

// Đổi chế độ hiển thị của đề đã tạo — chỉ người tạo đề (xem PUT /api/quizzes/:id).
export function updateQuizDisplayMode(quizId, displayMode) {
  return apiSend("PUT", `/api/quizzes/${quizId}`, { displayMode });
}

// Đóng/mở 1 đề (đóng = ẩn với học sinh, không làm online) — chỉ người tạo đề.
export function updateQuizClosed(quizId, closed) {
  return apiSend("PUT", `/api/quizzes/${quizId}`, { closed: !!closed });
}

export function deleteQuiz(quizId) {
  return apiSend("DELETE", `/api/quizzes/${quizId}`);
}

// Đáp án đúng của TOÀN BỘ câu trong 1 đề cố định — chỉ người TẠO ĐỀ mới xem
// được (dùng để in đáp án ra giấy), xem PQ-02 (Quiz3PrintAnswerKey).
export async function fetchQuizAnswerKey(quizId) {
  const data = await apiSend("GET", `/api/quizzes/${quizId}/answer-key`);
  return data.correctAnswers || {};
}

// ---- Phiếu trả lời giấy (chấm điểm bằng ảnh chụp, OMR) --------------------
// Không dùng apiSend() vì hàm đó ép cứng Content-Type: application/json —
// upload ảnh cần gửi FormData (trình duyệt tự set Content-Type multipart
// kèm boundary, không được set tay).
// sheetType: "a4d" (phiếu in từ app, mặc định) | "thpt3" (phiếu THPT 3 phần
// Bộ GD&ĐT). imageFile nhận ảnh hoặc PDF scan.
export async function scanAnswerSheet(quizId, imageFile, sheetType = "a4d") {
  const idToken = await getIdToken();
  const form = new FormData();
  form.append("quizId", quizId);
  form.append("sheetType", sheetType);
  form.append("image", imageFile);
  const res = await fetch(`${QUIZ3_API_BASE}/api/scan-answer-sheet`, {
    method: "POST",
    headers: { Authorization: `Bearer ${idToken}` },
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) handleSessionExpired();
  if (!res.ok) throw new Error(data.error || "Không quét được ảnh phiếu trả lời.");
  return data; // { quizId, questionIds, detectedAnswers, sheetType?, sheet? (chỉ thpt3) }
}

// Chấm điểm + lưu lịch sử — dùng chung logic /api/grade-quiz đã có (giống
// hệt luồng làm bài online trong Quiz3QuizPlayer.js), để luồng nộp bài bằng
// ảnh (Quiz3ScanSheet.js) không phải tự chấm điểm/tự ghi lịch sử riêng.
export function gradeQuiz({ quizId, questionIds, answers, startedAt }) {
  return apiSend("POST", "/api/grade-quiz", { quizId, questionIds, answers, startedAt });
}

export function shuffle(array) {
  const a = array.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ---- Chấm cả phòng (file PDF nhiều phiếu THPT 3 phần) — xem /api/scan-batch ở saola-quiz3-api/server.js
async function batchFetch(method, pathUrl, body) {
  const idToken = await getIdToken();
  const res = await fetch(`${QUIZ3_API_BASE}${pathUrl}`, {
    method,
    headers: { Authorization: `Bearer ${idToken}` },
    body,
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) handleSessionExpired();
  if (!res.ok) throw new Error(data.error || `Lỗi máy chủ (${res.status}).`);
  return data;
}
export function uploadScanBatch(file) {
  const form = new FormData();
  form.append("file", file);
  return batchFetch("POST", "/api/scan-batch", form); // { batchId, pages, name }
}
export function scanBatchPage(batchId, page) {
  return batchFetch("POST", `/api/scan-batch/${batchId}/page/${page}`); // { page, ok, sbd, maDe, part1..3, warnings }
}
// Ảnh JPEG 1 trang của file đã tải lên (Blob) — để lưu bài làm vào Thi khảo sát.
export async function scanBatchPageImage(batchId, page) {
  const idToken = await getIdToken();
  const res = await fetch(`${QUIZ3_API_BASE}/api/scan-batch/${batchId}/page/${page}/image`, { headers: { Authorization: `Bearer ${idToken}` } });
  if (res.status === 401) handleSessionExpired();
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || `Lỗi máy chủ (${res.status}).`); }
  return res.blob();
}
export function deleteScanBatch(batchId) {
  return batchFetch("DELETE", `/api/scan-batch/${batchId}`).catch(() => {});
}
